// src/App.tsx
import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "react-hot-toast";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { SHOW_STORE } from "./utils/featureFlags";
import "./App.css";
import BottomNav from "./components/BottomNav";
import ReferralUpsell from "./components/ReferralUpsell";
import InstallPrompt from "./components/InstallPrompt";
import AmplitudeSessionReplay from "./components/AmplitudeSessionReplay";
import ErrorBoundary from "./components/ErrorBoundary";
import Footer from "./components/Footer";
import GoogleTranslateWidget from "./components/GoogleTranslateWidget";
import IndexPolicy from "./components/IndexPolicy";
import LoadingPage from "./components/LoadingPage";
import Navbar from "./components/Navbar";
import AdsHeader from "./components/AdsHeader";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PricingProvider } from "./context/PricingContext";
import { useGuestChat, hasPendingGuestQuestion } from "./hooks/useGuestChat";
import { useInternationalOnly } from "./hooks/useInternationalOnly";

// Pages
// --- EAGER: the highest-traffic entry points, kept static so a first paint on
//     them costs no extra chunk round-trip.
//
//     These are NOT static because lazy would break prerendering. That was the
//     previous assumption here and it is wrong: react-snap resolves lazy routes
//     fine. Measured on a full `npm run build` after moving 7 pages to lazy,
//     all 22 routes prerendered with their own content and /about,
//     /contact-us, /founders, /privacy-policy and /terms-and-conditions came
//     out word-for-word identical to production (393/214/454/2163/1344 words).
//     scripts/prerender.js also hard-fails on an empty route, so a blank page
//     cannot reach a deploy. Splitting more of these is safe if it is worth it. ---
import AiAstrologerPage from "./pages/AiAstrologerPage";
import SeoLandingPage from "./components/SeoLandingPage";
import LoveCalculator from "./components/LoveCalculator";
import VedicCalculator from "./components/calculators/VedicCalculator";
import DailyHoroscope from "./components/calculators/DailyHoroscope";
import KundliChart from "./components/calculators/KundliChart";
import KundliMatch from "./components/calculators/KundliMatch";
/* Frozen pre-2026-09-11 copies, for the paid-ads page only. See the
   /free-kundli-matching route below. */
import SeoLandingPageLegacy from "./legacy/SeoLandingPageLegacy";
import KundliMatchLegacy from "./legacy/KundliMatchLegacy";
import {
  freeKundli,
  kundliMatching,
  thirumanaPorutham,
  dailyHoroscope,
  talkToAstrologer,
  methodology,
  astrologerDubai,
  astrologerUsa,
  usaHub,
  aiAstrologyPillar,
  loveCalculator,
  manglikCalculator,
  kaalSarpCalculator,
  sadeSatiCalculator,
  moonSignCalculator,
  nakshatraCalculator,
  risingSignCalculator,
  dashaCalculator,
  aiJyotish,
  hindi,
  freeAstrology,
  freeKundliMatchingAds,
} from "./data/landingPages";
// TAROT DISABLED (payment gateway):
// import { usaTarotLanding, uaeTarotLanding } from "./data/tarotLandingPages";
import { rashifalPages } from "./data/rashifalPages";
import { loveLanding, horoscopeLanding } from "./data/toolLandingPages";
import ArticlesSection from "./components/ArticlesSection";
import { MainSection } from "./components/MainSection";
import BlogPostPage from "./pages/BlogPostPage";
import HomePage from "./pages/HomePage";
import MarketPage from "./pages/MarketPage";
import CareerPage from "./pages/CareerPage";
import LovePage from "./pages/LovePage";
import MarriagePage from "./pages/MarriagePage";
import DownloadPage from "./pages/DownloadPage";
import BharatPage from "./pages/BharatPage";
import KundliPage from "./pages/KundliPage";
import WidgetsPage from "./pages/WidgetsPage";
import { festivalPages2027 } from "./data/festivalPages2027";
import { mahadashaPages } from "./data/mahadashaPages";
import { muhuratPages } from "./data/muhuratPages";
import { doshaPages } from "./data/doshaPages";
import { nakshatraPages } from "./data/nakshatraPages";
import GlobalPage from "./pages/GlobalPage";

import { usePageTracking } from "./hooks/usePageTracking";
import { useLanguageMode } from "./hooks/useLanguageMode";
import { reconcileJuspayPurchases } from "./utils/juspayCheckout";

// --- LAZY: auth-gated app pages (behind ProtectedRoute). Their real content is
//     never prerendered for SEO (crawlers hit the logged-out redirect), so
//     code-splitting them is SEO-safe and keeps them out of the initial bundle
//     that homepage/marketing visitors download. ---
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AdminCheckPage = lazy(() => import("./pages/AdminCheckPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const GuestChatPage = lazy(() => import("./pages/GuestChatPage"));
const CompatibilityScreen = lazy(() => import("./pages/CompatibilityScreen"));
const CreateProfilePage = lazy(() => import("./pages/CreateProfilePage"));
const FireSignsBlog = lazy(() => import("./pages/FireSignsBlog"));
const FullMoonBlog = lazy(() => import("./pages/FullMoonBlog"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PlanetaryTransitsBlog = lazy(() => import("./pages/PlanetaryTransitsBlog"));
const AiVsHumanAstrologyBlog = lazy(() => import("./pages/AiVsHumanAstrologyBlog"));
const AiAstrologyVsChatGptBlog = lazy(() => import("./pages/AiAstrologyVsChatGptBlog"));
const ManglikDoshaBlog = lazy(() => import("./pages/ManglikDoshaBlog"));
const DiwaliRashifal2026Blog = lazy(() => import("./pages/DiwaliRashifal2026Blog"));
const ChatGptSeKundliBlog = lazy(() => import("./pages/ChatGptSeKundliBlog"));
const KarwaChauth2026Blog = lazy(() => import("./pages/KarwaChauth2026Blog"));
const ShadiMuhurat2026Blog = lazy(() => import("./pages/ShadiMuhurat2026Blog"));
const November2026RashifalBlog = lazy(() => import("./pages/November2026RashifalBlog"));
const WeeklyHoroscopeNov9Blog = lazy(() => import("./pages/WeeklyHoroscopeNov9Blog"));
const WeeklyHoroscopeNov2Blog = lazy(() => import("./pages/WeeklyHoroscopeNov2Blog"));
const WeeklyHoroscopeOct26Blog = lazy(() => import("./pages/WeeklyHoroscopeOct26Blog"));
const WeeklyHoroscopeSep28Blog = lazy(() => import("./pages/WeeklyHoroscopeSep28Blog"));
const Dhanteras2026ShoppingBlog = lazy(() => import("./pages/Dhanteras2026ShoppingBlog"));
const KundliKaiseDekheBlog = lazy(() => import("./pages/KundliKaiseDekheBlog"));
const SadeSati2026Blog = lazy(() => import("./pages/SadeSati2026Blog"));
const ManglikDoshaBhangBlog = lazy(() => import("./pages/ManglikDoshaBhangBlog"));
const JanamNakshatraBlog = lazy(() => import("./pages/JanamNakshatraBlog"));
const KitneGunMilneChahiyeBlog = lazy(() => import("./pages/KitneGunMilneChahiyeBlog"));
const DevUthaniEkadashi2026Blog = lazy(() => import("./pages/DevUthaniEkadashi2026Blog"));
const MulankKaiseNikaleBlog = lazy(() => import("./pages/MulankKaiseNikaleBlog"));
const MahadashaKaiseDekheBlog = lazy(() => import("./pages/MahadashaKaiseDekheBlog"));
const Diwali2026UsaBlog = lazy(() => import("./pages/Diwali2026UsaBlog"));
const KundliForNrisBlog = lazy(() => import("./pages/KundliForNrisBlog"));
const UsBornDesiKundliBlog = lazy(() => import("./pages/UsBornDesiKundliBlog"));
const ManglikNriMarriageBlog = lazy(() => import("./pages/ManglikNriMarriageBlog"));
const Diwali2026PujaMuhuratBlog = lazy(() => import("./pages/Diwali2026PujaMuhuratBlog"));
const KundliMatchingBlog = lazy(() => import("./pages/KundliMatchingBlog"));
const LoveCalculatorBlog = lazy(() => import("./pages/LoveCalculatorBlog"));
const ProfileDashboardPage = lazy(() => import("./pages/ProfileDashboardPage"));
const QuickRechargePage = lazy(() => import("./pages/QuickRechargePage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const SubscriptionManagementPage = lazy(
  () => import("./pages/SubscriptionManagementPage"),
);
const UsagePage = lazy(() => import("./pages/UsagePage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const TarotReadingsPage = lazy(() => import("./pages/TarotReadingsPage"));
const TarotDeckWithLogin = lazy(() => import("./components/TarotDeckWithLogin"));
// TAROT DISABLED (payment gateway): only the /usa and /uae campaign pages are
// still off. Revive with the routes further down.
// const TarotLandingPage = lazy(() => import("./pages/TarotLandingPage"));
const ToolLandingPage = lazy(() => import("./pages/ToolLandingPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const YearEndBlog = lazy(() => import("./pages/YearEndBlog"));

// Split out of the main bundle: these were static imports, so every visitor
// (including someone who only ever sees the homepage) downloaded all of them.
// They sit inside the existing <Suspense fallback={null}> that already wraps
// <Routes>, and react-snap prerenders lazy routes correctly (verified: the
// already-lazy /FireSigns serves 67kB of prerendered HTML with its real h1),
// so this changes WHEN the code loads, not what crawlers or users see.
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const FoundersPage = lazy(() => import("./pages/FoundersPage"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage"));
const StorePage = lazy(() => import("./pages/StorePage"));
const PricingDetailsQuickRechargePage = lazy(() => import("./pages/PricingDetailsQuickRechargePage"));
const PanchangPage = lazy(() => import("./pages/PanchangPage"));
const PanchangDayPage = lazy(() => import("./pages/PanchangDayPage"));
const MuhuratPage = lazy(() => import("./pages/MuhuratPage"));

// Read at boot, BEFORE supabase-js strips the OAuth params off the URL. `?code=`
// is the PKCE callback (what Google sign-in actually returns, since the client is
// created with default options); `#access_token` is the older implicit flow.
// While that code is being exchanged, getSession() still reports NO session — so
// without this the app renders as a logged-out visitor for a second, which is the
// marketing homepage flashing past on the way back from Google.
const IS_OAUTH_RETURN =
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).has("code") ||
    window.location.hash.includes("access_token"));

// /chat is the one route a guest may enter — and only a NON-INDIAN one, who
// gets the teaser screen (ask 1 question → blurred answer → sign in to unlock).
// Everyone else is unchanged: signed in → the real ChatPage; Indian guest →
// bounced home, exactly what ProtectedRoute did for this route before.
function ChatRoute() {
  const { user } = useAuth();
  const { guestChatEnabled, resolvingGeo } = useGuestChat();

  if (user) return <ChatPage />;
  // Geo probe still in flight. Bailing out now would send a USA visitor home,
  // because userCountry reads 'IN' until it resolves.
  if (resolvingGeo) return null;
  if (guestChatEnabled) return <GuestChatPage />;
  return <Navigate to="/" replace />;
}

// /usa, /uae, /love and /horoscope are bought-traffic pages written for FOREIGN
// visitors — /usa pitches "an Indian astrologer, from the USA", /uae prices in
// AED. They were indexed and sitemapped with no gate at all, so Indian organic
// visitors were landing on them. Same three-way as ChatRoute: wait for the geo
// probe rather than assume 'IN' (which is what userCountry reads until it
// resolves), then show or send home.
function InternationalRoute({ children }: { children: React.ReactElement }) {
  const { allowed, resolvingGeo } = useInternationalOnly();

  if (resolvingGeo) return null;
  if (allowed) return children;
  return <Navigate to="/" replace />;
}

/* Deliberately tiny and dependency-free: this renders in the gap before a route
   chunk exists, so it must not pull one in itself. */
function RouteFallback() {
  return (
    <div
      style={{
        /* Full viewport, not 60vh: with a shorter fallback the footer sat
           on screen during the route-chunk swap and then jumped down when
           the real page rendered, scoring CLS 0.396 on blog posts (audit
           perf item 3). At 100vh the footer starts below the fold, so its
           move no longer counts as a visible shift. */
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
      role="status"
      aria-label="Loading"
    >
      <span className="app-route-spin" />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfiles } = useAuth();
  // 2. Add this Effect
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]); // Runs every time the URL path changes

  // A Juspay top-up that confirmed after its screen stopped waiting gets its
  // Purchase event here, on the next visit or when the tab is back in view.
  useEffect(() => {
    if (!user?.id) return;
    const uid = user.id;
    const check = () => {
      if (document.visibilityState === "visible") reconcileJuspayPurchases(uid).catch(() => {});
    };
    check();
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, [user?.id]);

  // GUEST CHAT HAND-OFF. Someone who asked a question on the teaser and then
  // signed in has to reach their answer, and it can't be generated without a
  // birth chart — so send them to create a profile first, then to the chat
  // (where the parked question sends itself).
  //
  // AuthContext's own post-login redirect can't be relied on here: it only fires
  // when the callback URL carries an `access_token` HASH, but the PKCE flow
  // returns `?code=`. So a Google sign-in never sets `justLoggedIn` and never
  // redirects — which is how a signed-in guest ended up staring at the empty
  // "create a profile" view on /chat with their question stranded.
  //
  // While this is pending we render a loader INSTEAD of the routed page, so the
  // homepage doesn't flash past while the session is still being exchanged.
  //
  // All of it is gated on the parked question, which ONLY the (non-Indian) guest
  // teaser ever writes, so no other user's login lands anywhere new or waits on
  // anything. Runs at most once.
  const [guestHandoffDone, setGuestHandoffDone] = useState(false);
  const guestHandoffPending =
    !guestHandoffDone && hasPendingGuestQuestion() && (IS_OAUTH_RETURN || !!user);

  useEffect(() => {
    if (!guestHandoffPending) return;

    // Session not resolved yet (PKCE code still being exchanged). Wait — but
    // never hold the page hostage: if the login never completes, let go.
    if (!user || !userProfiles) {
      const bail = window.setTimeout(() => setGuestHandoffDone(true), 10000);
      return () => window.clearTimeout(bail);
    }

    const target = userProfiles.length === 0 ? "/profiles/new" : "/chat";
    if (location.pathname !== target) navigate(target, { replace: true });
    setGuestHandoffDone(true);
  }, [guestHandoffPending, user, userProfiles, location.pathname, navigate]);
  /* Single-goal landing pages: bought or written for one action, so every nav
     item, install nudge and footer link is a way to spend the visit on
     something else. Add /love and /marriage here when they land and they
     inherit the whole treatment.

     Matched on pathname rather than a prop because <Navbar /> and <Footer />
     live here, above <Routes>, so a route element cannot reach them. */
  const FOCUSED_LANDING_ROUTES = [
    "/free-kundli-matching",
    "/career",
    "/love-astrology",
    "/marriage",
    "/foreign",
    "/bharat",
    "/kundli",
  ];
  const isAdsLanding = FOCUSED_LANDING_ROUTES.includes(
    location.pathname.replace(/\/$/, ""),
  );

  /* The site footer is a wall of links out, and its closing banner is generic
     English. These pages close with their own CTA card in their own voice and
     carry a minimal legal footer instead. */
  const showFooter =
    location.pathname !== "/chat" &&
    location.pathname !== "/wallet" &&
    !isAdsLanding;
  const isChatPage = location.pathname === "/chat";

  // App-parity bottom nav shows for signed-in users on every screen except
  // chat and wallet; reserve space at the bottom on mobile so it doesn't cover
  // content. (Must match BottomNav's own hidden-route list.)
  // Focused ad landings are excluded too. They exist to drive ONE action, and
  // the bottom nav both adds five exits and physically covers their sticky
  // CTA on a phone.
  const showBottomNav =
    !!user &&
    location.pathname !== "/chat" &&
    location.pathname !== "/wallet" &&
    !isAdsLanding;

  usePageTracking();
  useLanguageMode();

  return (
    <>
      <IndexPolicy />
      {/* This renders the ONE Navbar for the whole app. Page components must NOT
          render their own: the four blog articles and BlogPostPage each did,
          which shipped two <nav> elements in the prerendered HTML.

          /free-kundli-matching and /career SWAP it for a stripped header
          (brand + language only) rather than adding one, for the same reason:
          exactly one <nav> ships. See FOCUSED_LANDING_ROUTES above. */}
      {isAdsLanding ? <AdsHeader /> : <Navbar />}

      <div className={`pageContainer${showBottomNav ? " hasBottomNav" : ""}`}>
        <div style={{ flex: 1 }}>
          <ErrorBoundary>
          {/* fallback={null} rendered NOTHING while a route chunk downloaded.
              Every route here is lazy, so a first-time visitor got the bare
              page background - a blank purple sheet - until the chunk landed.
              It looked fine on reload only because the chunk was then cached.
              A visible loader makes the gap read as loading rather than broken. */}
          <Suspense fallback={<RouteFallback />}>
          {guestHandoffPending ? (
            <LoadingPage message="Getting your answer ready..." />
          ) : (
          <Routes>
            <Route path="/" element={<HomePage />} />
            {/* Language modes: same landing, opened persistently in that language
                (see useLanguageMode). Marketing deep-links: /kn /gu /bn /ar.
                /ar is right-to-left (useLanguageMode sets dir="rtl"). */}
            <Route path="/kn" element={<HomePage />} />
            <Route path="/gu" element={<HomePage />} />
            <Route path="/bn" element={<HomePage />} />
            <Route path="/ar" element={<HomePage />} />
            {/* /market is now /india. The old path stays as a redirect rather
                than a 404 so existing links, bookmarks and any ad creative
                still pointing at it land on the real page.

                This is a client-side redirect, not a 301. For search, add a
                301 /market -> /india in the Amplify console rewrites so the
                old URL's ranking signal actually transfers; until then Google
                sees a 200 that renders /india. */}
            <Route path="/market" element={<Navigate to="/india" replace />} />
            {/* The page itself: /market's layout with the distractions removed.
                No calculator, and the nav and footer drop Blog, Calculators,
                the Free tools rows and Shop (see Navbar and Footer). */}
            <Route path="/india" element={<MarketPage focused />} />
            <Route path="/global" element={<GlobalPage />} />
            {/* Hidden while the PayU restriction stands. Redirects rather
                than falling through to the bare 404, so an old link or a
                bookmark lands somewhere real. */}
            <Route
              path="/store"
              element={SHOW_STORE ? <StorePage /> : <Navigate to="/" replace />}
            />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/ai-astrologer" element={<AiAstrologerPage />} />
            <Route
              path="/free-kundli"
              element={<SeoLandingPage config={freeKundli} tool={<KundliChart />} />}
            />
            <Route
              path="/kundli-matching"
              element={<SeoLandingPage config={kundliMatching} tool={<KundliMatch />} />}
            />
            {/* Career astrology. INDEXED: the career cluster already earns
                145 clicks / 1,418 impressions a quarter with no page behind
                it, all landing on the homepage. Hinglish conversion copy with
                an English SEO block, see CareerPage.tsx. */}
            <Route path="/career" element={<CareerPage />} />
            {/* Love, marriage timing and kundli compatibility. NOT /love, which
                is the geo-gated page for foreign visitors; this one is written
                for the Indian visitor that gate turns away. Slug from GSC: the
                love cluster takes ~90 clicks a quarter with no page behind it,
                while "compatibility" earns nothing and is /kundli-matching's
                word anyway. */}
            <Route path="/love-astrology" element={<LovePage />} />
            <Route path="/marriage" element={<MarriagePage />} />
            {/* Geo-gated like /usa and /uae: this page is bought for FOREIGN
                traffic, USA in particular, so an Indian visitor who reaches it
                is sent home rather than shown a pitch for a market they are not
                in. InternationalRoute waits for the geo probe before deciding,
                and never gates the prerender crawl. */}
            <Route
              path="/foreign"
              element={
                <InternationalRoute>
                  <DownloadPage />
                </InternationalRoute>
              }
            />
            {/* The India sibling of /foreign. NOT geo-gated: unlike the
                foreign pitch, this one is also right for the diaspora
                abroad, so gating it to IN would turn away part of its own
                audience. */}
            <Route path="/bharat" element={<BharatPage />} />
            <Route path="/kundli" element={<KundliPage />} />
            <Route path="/widgets" element={<WidgetsPage />} />
            {/* 2027 festival date pages (division C2): configs in
                src/data/festivalPages2027.ts, one route each. */}
            {festivalPages2027.map((f) => (
              <Route
                key={f.path}
                path={f.path}
                element={<SeoLandingPage config={f.config} />}
              />
            ))}
            {/* C3 muhurat-by-type monthly pages + C4 dosha and 27 nakshatra glossary pages. */}
            {[...muhuratPages, ...doshaPages, ...nakshatraPages].map((f) => (
              <Route
                key={f.path}
                path={f.path}
                element={<SeoLandingPage config={f.config} />}
              />
            ))}
            {/* C4 glossary round 1: the 9 graha mahadasha pages. */}
            {mahadashaPages.map((f) => (
              <Route
                key={f.path}
                path={f.path}
                element={<SeoLandingPage config={f.config} />}
              />
            ))}
            {/* PAID ADS ONLY. The /kundli-matching page EXACTLY as it stood
                before the 2026-09-11 SEO rebuild, restored from commit 9034468.
                Marketing owns this URL; /kundli-matching is the one that ranks
                and must not be edited for campaigns.

                It renders its own forked copies under src/legacy/ rather than
                the live components, because the two versions share 152 CSS
                class names and plain CSS is global, so loading both unprefixed
                would break the live page. The legacy classes are prefixed
                lkm-/lbi- so the two can never touch.

                noindex by omission: absent from INDEXABLE in IndexPolicy.tsx
                and ALLOW in public/index.html. Also absent from ROUTES in
                scripts/gen-sitemap.js, and nothing links to it. Do not add it
                to any of those three. */}
            <Route
              path="/free-kundli-matching"
              element={
                <SeoLandingPageLegacy
                  config={freeKundliMatchingAds}
                  tool={<KundliMatchLegacy />}
                />
              }
            />
            {/* South Indian matching. A SEPARATE system from Ashtakoot, not a
                synonym, so it gets its own page rather than competing with
                /kundli-matching for the same queries. */}
            <Route
              path="/thirumana-porutham"
              element={<SeoLandingPage config={thirumanaPorutham} tool={<KundliMatch />} />}
            />
            <Route
              path="/love-calculator"
              element={<SeoLandingPage config={loveCalculator} tool={<LoveCalculator />} />}
            />
            <Route
              path="/manglik-calculator"
              element={
                <SeoLandingPage
                  config={manglikCalculator}
                  tool={<VedicCalculator kind="mangal_dosha" cta="Am I Manglik?" />}
                />
              }
            />
            <Route
              path="/kaal-sarp-dosha"
              element={
                <SeoLandingPage
                  config={kaalSarpCalculator}
                  tool={<VedicCalculator kind="kaal_sarp" cta="Check my chart" />}
                />
              }
            />
            <Route
              path="/sade-sati-calculator"
              element={
                <SeoLandingPage
                  config={sadeSatiCalculator}
                  tool={<VedicCalculator kind="sade_sati" cta="Am I in Sade Sati?" />}
                />
              }
            />
            <Route
              path="/moon-sign-calculator"
              element={
                <SeoLandingPage
                  config={moonSignCalculator}
                  tool={<VedicCalculator kind="rashi" cta="Find my moon sign" />}
                />
              }
            />
            <Route
              path="/nakshatra-calculator"
              element={
                <SeoLandingPage
                  config={nakshatraCalculator}
                  tool={<VedicCalculator kind="nakshatra" cta="Find my nakshatra" />}
                />
              }
            />
            <Route
              path="/rising-sign-calculator"
              element={
                <SeoLandingPage
                  config={risingSignCalculator}
                  tool={<VedicCalculator kind="rising_sign" cta="Find my rising sign" />}
                />
              }
            />
            <Route
              path="/mahadasha-calculator"
              element={
                <SeoLandingPage
                  config={dashaCalculator}
                  tool={<VedicCalculator kind="dasha" cta="Show my dasha timeline" />}
                />
              }
            />
            <Route
              path="/daily-horoscope"
              element={
                <SeoLandingPage config={dailyHoroscope} tool={<DailyHoroscope />} />
              }
            />
            <Route path="/talk-to-astrologer" element={<SeoLandingPage config={talkToAstrologer} />} />
            {/* Hinglish jyotish page (item 23): the searcher uses the Hindi
                word; the head terms live here, /ai-astrologer keeps the
                English astrologer cluster. */}
            <Route path="/ai-jyotish" element={<SeoLandingPage config={aiJyotish} />} />
            <Route path="/hi" element={<SeoLandingPage config={hindi} />} />
            <Route path="/free-astrology" element={<SeoLandingPage config={freeAstrology} />} />
            {/* 12 rashifal pages (item 34), one per sign, data-driven from
                rashifalPages.ts. The path is the config's own URL path, so
                the data file stays the single source of truth. */}
            {rashifalPages.map((cfg) => (
              <Route
                key={cfg.url}
                path={cfg.url.replace("https://astroaura.ai", "")}
                element={<SeoLandingPage config={cfg} />}
              />
            ))}
            {/* Geo-targeted landing pages. The site carried no signal at all for
                the Gulf or the US, the homepage never says "Dubai", "UAE" or
                "NRI", so google.ae and google.com had nothing to match. */}
            <Route path="/vedic-astrologer-dubai" element={<SeoLandingPage config={astrologerDubai} />} />
            <Route path="/indian-astrologer-usa" element={<SeoLandingPage config={astrologerUsa} />} />
            {/* TAROT DISABLED (payment gateway).
                /usa and /uae were tarot campaign pages end to end - title, H1,
                every prose block and every FAQ - so dropping the draw widget
                would not have made them compliant.

                They are NOT removed, because both are live paid-campaign
                destinations, sit in the sitemap at priority 0.9 and are
                indexed: a 404 would burn ad spend and lose the URLs. They now
                serve GlobalPage, which is written for the same international
                audience (same hero, trust points, use cases and international
                testimonials TarotLandingPage used) and contains no tarot.

                TO REVIVE: delete the two GlobalPage routes below and uncomment
                the two original routes. Also restore the config import and the
                TarotLandingPage lazy import above.

            <Route path="/usa" element={<InternationalRoute><TarotLandingPage config={usaTarotLanding} /></InternationalRoute>} />
            <Route path="/uae" element={<InternationalRoute><TarotLandingPage config={uaeTarotLanding} /></InternationalRoute>} />
            */}
            {/* USA batch 1 (2026-10-21): /usa is no longer GlobalPage with a
                canonical into the unindexed /global. It is a real NRI hub
                (usaHub in landingPages.ts), self-canonical at /usa, and
                UNGATED: the page is written for NRIs but harmless to an
                Indian visitor, and removing InternationalRoute removes the
                geo-probe dependency from the prerender. /uae keeps the old
                arrangement until its own rebuild.
                Tarot (2026-11-03, Arjun): both pages carry the homepage deck,
                which still hides itself in India. */}
            <Route
              path="/usa"
              element={
                <SeoLandingPage
                  config={usaHub}
                  afterIntro={
                    <Suspense fallback={null}>
                      <TarotDeckWithLogin />
                    </Suspense>
                  }
                />
              }
            />
            <Route
              path="/ai-astrology"
              element={<SeoLandingPage config={aiAstrologyPillar} />} />
            <Route path="/uae" element={<InternationalRoute><GlobalPage withTarot /></InternationalRoute>} />
            {/* Same homepage-shaped campaign layout, with the love calculator
                and the daily horoscope in the slot the kundli form occupies.
                Distinct from /love-calculator and /daily-horoscope, which are
                the SeoLandingPage prose pages built to rank; these two are
                built to convert paid traffic. */}
            <Route
              path="/love"
              element={
                <InternationalRoute>
                  <ToolLandingPage
                    config={loveLanding}
                    tool={(gate) => <LoveCalculator authGate={gate} />}
                  />
                </InternationalRoute>
              }
            />
            <Route
              path="/horoscope"
              element={
                <InternationalRoute>
                  <ToolLandingPage
                    config={horoscopeLanding}
                    tool={(gate) => <DailyHoroscope authGate={gate} />}
                  />
                </InternationalRoute>
              }
            />
            <Route path="/founders" element={<FoundersPage />} />
            <Route path="/how-it-works" element={<MainSection />} />
            {/* Methodology / trust page (item 24): E-E-A-T signals the audit
                asked for; the named-reviewer block lands when a real reviewer
                is provided. */}
            <Route path="/methodology" element={<SeoLandingPage config={methodology} />} />
            <Route path="/blog" element={<ArticlesSection />} />
            {/* Static slug, declared before the dynamic CMS route. React Router
                ranks the literal segment above /blog/:slug so they do not collide. */}
            <Route
              path="/blog/ai-astrology-vs-human-astrology"
              element={<AiVsHumanAstrologyBlog />}
            />
            <Route
              path="/blog/kundli-matching-gun-milan-explained"
              element={<KundliMatchingBlog />}
            />
            <Route
              path="/blog/love-calculator-what-the-score-means"
              element={<LoveCalculatorBlog />}
            />
            <Route
              path="/blog/ai-astrology-vs-chatgpt"
              element={<AiAstrologyVsChatGptBlog />}
            />
            <Route
              path="/blog/manglik-dosha-explained"
              element={<ManglikDoshaBlog />}
            />
            <Route
              path="/blog/diwali-2026-rashifal"
              element={<DiwaliRashifal2026Blog />}
            />
            <Route
              path="/blog/diwali-2026-usa"
              element={<Diwali2026UsaBlog />}
            />
            <Route
              path="/blog/diwali-2026-puja-muhurat"
              element={<Diwali2026PujaMuhuratBlog />}
            />
            <Route
              path="/blog/kundli-for-nris"
              element={<KundliForNrisBlog />}
            />
            <Route
              path="/blog/birth-chart-us-born-desis"
              element={<UsBornDesiKundliBlog />}
            />
            <Route
              path="/blog/manglik-dosha-nri-marriage"
              element={<ManglikNriMarriageBlog />}
            />
            <Route
              path="/blog/chatgpt-se-kundli"
              element={<ChatGptSeKundliBlog />}
            />
            <Route
              path="/blog/karwa-chauth-2026"
              element={<KarwaChauth2026Blog />}
            />
            <Route
              path="/blog/shadi-muhurat-nov-dec-2026"
              element={<ShadiMuhurat2026Blog />}
            />
            <Route
              path="/blog/weekly-horoscope-nov-9-15-2026"
              element={<WeeklyHoroscopeNov9Blog />}
            />
            <Route
              path="/blog/weekly-horoscope-nov-2-8-2026"
              element={<WeeklyHoroscopeNov2Blog />}
            />
            <Route
              path="/blog/weekly-horoscope-26-oct-1-nov-2026"
              element={<WeeklyHoroscopeOct26Blog />}
            />
            <Route
              path="/blog/weekly-horoscope-28-sep-4-oct-2026"
              element={<WeeklyHoroscopeSep28Blog />}
            />
            <Route path="/blog/kundli-kaise-dekhe" element={<KundliKaiseDekheBlog />} />
            <Route path="/blog/sade-sati-2026-27" element={<SadeSati2026Blog />} />
            <Route path="/blog/manglik-dosha-bhang" element={<ManglikDoshaBhangBlog />} />
            <Route path="/blog/janam-nakshatra-kaise-jaane" element={<JanamNakshatraBlog />} />
            <Route path="/blog/kitne-gun-milne-chahiye" element={<KitneGunMilneChahiyeBlog />} />
            <Route path="/blog/dev-uthani-ekadashi-2026-shadi-season" element={<DevUthaniEkadashi2026Blog />} />
            <Route path="/blog/mulank-kaise-nikale" element={<MulankKaiseNikaleBlog />} />
            <Route path="/blog/mahadasha-kaise-dekhe" element={<MahadashaKaiseDekheBlog />} />
            <Route
              path="/blog/dhanteras-2026-rashi-shopping"
              element={<Dhanteras2026ShoppingBlog />}
            />
            <Route
              path="/blog/november-2026-rashifal"
              element={<November2026RashifalBlog />}
            />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/BlogPage" element={<BlogPostPage />} />
            {/* Blog articles: public (were behind ProtectedRoute -> invisible to
                crawlers). Now indexable content-marketing pages. */}
            <Route path="/blog/fire-signs-in-vedic-astrology" element={<FireSignsBlog />} />
            <Route path="/blog/full-moon-purnima-astrology" element={<FullMoonBlog />} />
            <Route path="/blog/planetary-transits-explained" element={<PlanetaryTransitsBlog />} />
            <Route path="/FireSigns" element={<FireSignsBlog />} />
            <Route path="/FullMoonBlog" element={<FullMoonBlog />} />
            <Route path="/YearEndPredictions" element={<YearEndBlog />} />
            <Route path="/PlanetaryTransits" element={<PlanetaryTransitsBlog />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route
              path="/pricing"
              element={<PricingDetailsQuickRechargePage />}
            />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="/panchang" element={<PanchangPage />} />
            <Route path="/panchang/:slug" element={<PanchangDayPage />} />
            <Route path="/muhurat" element={<MuhuratPage />} />

            {/* Auth handled inside ChatRoute (guests may see the teaser). */}
            <Route path="/chat" element={<ChatRoute />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/profiles" element={<ProfileDashboardPage />} />
              <Route path="/profiles/new" element={<CreateProfilePage />} />
              <Route path="/reports/:profileId" element={<ReportsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route
                path="/compatibility-report"
                element={<CompatibilityScreen />}
              />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route
                path="/subscription-management"
                element={<SubscriptionManagementPage />}
              />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/orders" element={<OrdersPage />} />
              {/* Tarot history lives on its own screen so the homepage section
                  stays a deck and a reading, not an archive. */}
              <Route path="/tarot-readings" element={<TarotReadingsPage />} />
              <Route path="/referral" element={<ReferralPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/quick-recharge" element={<QuickRechargePage />} />
            </Route>

            {/* Hidden Admin - guarded by admin-only route. No links in UI. */}
            <Route element={<ProtectedRoute requireAdmin={true} />}>
              <Route path="/admin" element={<AdminPage />} />
            </Route>

            {/* Debug route to verify admin state */}
            <Route element={<ProtectedRoute />}>
              <Route path="/admin-check" element={<AdminCheckPage />} />
            </Route>

            <Route path="*" element={<h1>404: Page Not Found</h1>} />
          </Routes>
          )}
          </Suspense>
          </ErrorBoundary>
        </div>

        {/* Footer */}
        {showFooter && <Footer />}
      </div>

      <BottomNav />

      {/* Post-payment refer & earn upsell (listens for aura:payment-success). */}
      <ReferralUpsell />

      {/* Controlled PWA install nudge (after 2 min or a chat; once, then backs off).
          Suppressed on the focused landing pages: they are bought per click and
          sold on one action, and a full-width "Install Aura AI" sheet over the
          form is the most expensive distraction on the page. */}
      {!isAdsLanding && <InstallPrompt />}

      {/* Amplitude Session Replay + heatmaps — attaches for non-Indian users only. */}
      <AmplitudeSessionReplay />

      <GoogleTranslateWidget />

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          style: {
            zIndex: 10001,
          },
        }}
        containerStyle={{
          zIndex: 10001,
        }}
      />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <AuthProvider>
          <PricingProvider>
            <AppContent />
          </PricingProvider>
        </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}
